import { FC, useState, useEffect, useCallback, useMemo } from 'react';
import { Button, Modal, Badge, OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
    CheckCircle,
    AlertTriangle,
    Info,
    XCircle,
    Eye,
    Calendar,
    Star,
    CheckCheck,
    X,
    Link2,
    MessageSquare,
    ExternalLink,
    UserCheck,
    CalendarDays,
    AlarmClock,
    Target,
    TrendingUp,
} from 'lucide-react';
import { INotificationItem } from '../core/_models';
import { acknowledgeNotification } from '../core/_request';
import { ApiResponse } from '../core/_models';
import { envConfig } from '../../../utils/config';
import { CustomDateFormate, parseDate } from '../../../utils/date';

interface NotificationDetailModalProps {
    show: boolean;
    onHide: () => void;
    notification: INotificationItem | null;
    setSelectedNotification: (data: INotificationItem) => void
}

// Constants
const TYPE_CONFIG = {
    success: { icon: CheckCircle, gradient: 'linear-gradient(135deg, #01724c 0%, #00d692 100%)', color: '#10b981', bg: '#f0fdf4', border: '#10b981', label: 'Success' },
    warning: { icon: AlertTriangle, gradient: 'linear-gradient(135deg, #bc6100 0%, #ffc400 100%)', color: '#f59e0b', bg: '#fffbeb', border: '#f59e0b', label: 'Warning' },
    danger: { icon: XCircle, gradient: 'linear-gradient(135deg, #8a0000 0%, #e60303 100%)', color: '#ef4444', bg: '#fef2f2', border: '#ef4444', label: 'Critical' },
    info: { icon: Info, gradient: 'linear-gradient(135deg, #022969 0%, #4f86ff 100%)', color: '#3b82f6', bg: '#eff6ff', border: '#3b82f6', label: 'Information' },
};

const PRIORITY_CONFIG = {
    high: { color: '#ef4444', bg: '#fee2e2', label: 'High Priority', icon: '🔥' },
    medium: { color: '#f59e0b', bg: '#ffedd5', label: 'Medium Priority', icon: '⚡' },
    low: { color: '#10b981', bg: '#d1fae5', label: 'Low Priority', icon: '✓' },
};

const NotificationDetailModal: FC<NotificationDetailModalProps> = ({
    show,
    onHide,
    notification,
    setSelectedNotification
}) => {
    // All hooks must be called before any conditional returns
    const [isAcknowledged, setIsAcknowledged] = useState(false);
    const [isAcknowledging, setIsAcknowledging] = useState(false);

    const getRelativeTime = useCallback((date: Date) => {
        const now = new Date();
        const diffMins = Math.floor((now.getTime() - date.getTime()) / 60000);
        const diffHours = Math.floor(diffMins / 60);
        const diffDays = Math.floor(diffHours / 24);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
        if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
        if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
        if (diffDays < 30) return `${Math.floor(diffDays / 7)} week${Math.floor(diffDays / 7) !== 1 ? 's' : ''} ago`;
        return `on ${CustomDateFormate(date)}`;
    }, []);

    const formatDateTime = useCallback((dateString: string) => {
        const date = parseDate(dateString);
        if (!date) {
            return { date: '', time: '', relative: '' };
        }
        return {
            date: CustomDateFormate(date),
            time: date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
            relative: getRelativeTime(date),
        };
    }, [getRelativeTime]);

    const handleAcknowledge = useCallback(async () => {
        if (!notification) return;

        setIsAcknowledging(true);
        try {
            const response = await acknowledgeNotification({ id: notification.id })

            if (response.status === "ok") {
                setIsAcknowledged(true);
                setSelectedNotification({ ...notification, is_acknowledged: response.data.is_acknowledged, acknowledged_at: response.data.acknowledged_at })
            } else {
                console.error("Acknowledge failed:", response.message);
            }
        } catch (error) {
            console.error("Error acknowledging notification:", error);
        } finally {
            setIsAcknowledging(false);
        }
    }, [notification]);

    // Effects
    useEffect(() => {
        if (notification) {
            setIsAcknowledged(notification.is_acknowledged || false);
        }
    }, [notification,]);

    // Memoized values
    const dateTime = useMemo(() => {
        if (!notification) return { date: '', time: '', relative: '' };
        return formatDateTime(notification.creation_date);
    }, [notification, formatDateTime]);

    const typeConfig = useMemo(() => {
        if (!notification) return TYPE_CONFIG.info;
        return TYPE_CONFIG[notification.notification_type?.toLowerCase() as keyof typeof TYPE_CONFIG] || TYPE_CONFIG.info;
    }, [notification]);

    const priorityConfig = useMemo(() => {
        if (!notification) return null;
        return PRIORITY_CONFIG[notification.priority?.toLowerCase() as keyof typeof PRIORITY_CONFIG];
    }, [notification]);

    const canAcknowledge = useMemo(() => {
        if (!notification) return false;
        return notification.is_important;
    }, [notification]);

    const TypeIcon = typeConfig.icon;

    const dateValue = useMemo(() => {
        if (!notification) return false;
        return notification.type === "evaluation_completed"
            ? notification.extra.evaluationCompletionDate
            : notification.extra.projectDueDate;
    }, [notification]);


    // Early return after all hooks
    if (!notification) return null;

    return (
        <Modal
            show={show}
            onHide={onHide}
            size="xl"
            centered
            backdrop="static"
            className="notification-detail-modal"
            dialogClassName="modal-dialog-scrollable"
        >
            <div className="position-relative">
                {/* Header Banner */}
                <div
                    className="position-relative overflow-hidden"
                    style={{
                        background: typeConfig.gradient,
                        borderTopLeftRadius: 'calc(0.7rem - 1px)',
                        borderTopRightRadius: 'calc(0.7rem - 1px)',
                    }}
                >
                    <div className="px-4 pt-4 pb-5 text-white position-relative z-1">
                        <div className="d-flex justify-content-between align-items-start">
                            <div className="d-flex gap-3 align-items-center">
                                <div
                                    className="rounded-circle bg-white bg-opacity-25 d-flex align-items-center justify-content-center p-3"
                                >
                                    <TypeIcon size={28} className="text-white" />
                                </div>
                                <div>
                                    <h3 className="fw-bold mb-1 text-white">{notification.title} {notification.extra?.projectShortName && `- ${notification.extra.projectShortName}`}</h3>
                                </div>
                            </div>
                            <button
                                onClick={onHide}
                                className="btn btn-sm rounded-circle bg-white bg-opacity-25 p-3 border-0 text-white d-flex align-items-center justify-content-center"
                                style={{ width: '36px', height: '36px' }}
                            >
                                <X size={18} />
                            </button>
                        </div>
                    </div>
                    <div
                        className="position-absolute bottom-0 start-0 w-100"
                        style={{
                            height: '4px',
                            background: 'linear-gradient(90deg, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0) 100%)'
                        }}
                    />
                </div>

                <Modal.Body className="p-0">
                    <div className="p-4">
                        <div className="animate-fade-in">
                            {/* Meta Information Cards */}
                            <div className="row g-3 mb-4 align-items-stretch">
                                <div className="col-md-6 d-flex">
                                    <div className="bg-light rounded-3 p-3 w-100">
                                        <div className="d-flex align-items-center gap-4 mb-2">
                                            <div className="rounded-circle bg-primary bg-opacity-10 p-3">
                                                <CalendarDays size={18} className="text-primary" />
                                            </div>
                                            <div>
                                                <p className="text-muted small fw-semibold mb-1">DATE & TIME</p>
                                                <div className="fw-semibold">{dateTime.date} {dateTime.time}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Message Content */}
                            <div className="mb-4">
                                <h6 className="fw-semibold mb-3 d-flex align-items-center gap-2">
                                    <MessageSquare size={16} className="text-primary" />
                                    Message
                                </h6>
                                <div
                                    className="bg-light rounded-4 p-4"
                                    style={{ borderLeft: `4px solid ${typeConfig.color}` }}
                                >
                                    <p className="mb-0 lh-lg" style={{ whiteSpace: 'pre-wrap', fontSize: '0.95rem' }}>
                                        {notification.message}
                                    </p>
                                </div>
                            </div>

                            {/* Additional Information Section */}
                            {(notification.action_url || (notification.extra && Object.keys(notification.extra).length > 0)) && (
                                <div className="mb-4">
                                    <h6 className="fw-semibold mb-3 d-flex align-items-center gap-2">
                                        <Info size={16} className="text-primary" />
                                        Additional Information
                                    </h6>
                                    <div className="bg-light rounded-4 p-4">
                                        <div className="row g-3">
                                            {notification.action_url && (
                                                <div className="col-md-6">
                                                    <div className="d-flex align-items-center gap-2">
                                                        <Link2 size={14} className="text-primary" />
                                                        <span className="text-muted small">Evaluation Link:</span>
                                                        <a
                                                            href={`${notification.action_url}/evaluation`}
                                                            className="small text-decoration-none"
                                                        >
                                                            {`${notification.action_url}`.length > 30
                                                                ? `${notification.action_url}`.substring(0, 30) + '...'
                                                                : `${notification.action_url}`}
                                                            <ExternalLink size={10} className="ms-1" />
                                                        </a>
                                                    </div>
                                                </div>
                                            )}

                                            {notification.extra && (
                                                <>
                                                    {(dateValue) && (
                                                        <div className="col-md-6">
                                                            <div className="d-flex align-items-center gap-2">
                                                                <Calendar size={14} className="text-muted" />
                                                                <span className="text-muted small">{notification.type === "evaluation_completed" ? "Evaluation Completion Date:" : "Project Due Date:"}</span>
                                                                <span className="small text-danger fw-semibold">
                                                                    {CustomDateFormate(dateValue)}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    )}
                                                    {notification.extra.projectComplitionDate && (
                                                        <div className="col-md-6">
                                                            <div className="d-flex align-items-center gap-2">
                                                                <Calendar size={14} className="text-muted" />
                                                                <span className="text-muted small">Project Completion Date:</span>
                                                                <span className="small text-danger fw-semibold">
                                                                    {CustomDateFormate(notification.extra.projectComplitionDate)}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Action Buttons - Only show if notification is important/starred */}
                            {canAcknowledge && !isAcknowledged && (
                                <div className="d-flex gap-3 flex-wrap mt-4 pt-3 border-top">
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip>Confirm you have read and understood this notification</Tooltip>}
                                    >
                                        <Button
                                            variant="light-success"
                                            onClick={handleAcknowledge}
                                            disabled={isAcknowledging}
                                            className="d-flex align-items-center gap-2 px-4 py-2 rounded-3"
                                        >
                                            {isAcknowledging ? (
                                                <>
                                                    <div className="spinner-border spinner-border-sm me-2" />
                                                    Acknowledging...
                                                </>
                                            ) : (
                                                <>
                                                    <CheckCheck size={18} />
                                                    Mark as Acknowledged
                                                </>
                                            )}
                                        </Button>
                                    </OverlayTrigger>
                                </div>
                            )}

                            {/* Acknowledgment info */}
                            {isAcknowledged && notification.acknowledged_at && (
                                <div className="mt-4 p-3 bg-success bg-opacity-10 rounded-4">
                                    <div className="d-flex align-items-center gap-2">
                                        <UserCheck size={18} className="text-success" />
                                        <small className="text-success">
                                            You acknowledged this notification on {CustomDateFormate(notification.acknowledged_at)}
                                        </small>
                                    </div>
                                </div>
                            )}

                            {/* Info message when notification cannot be acknowledged */}
                            {/* {!canAcknowledge && !isAcknowledged && (
                                <div className="mt-4 p-3 bg-info bg-opacity-10 rounded-4">
                                    <div className="d-flex align-items-center gap-2">
                                        <Info size={18} className="text-info" />
                                        <small className="text-info">
                                            This notification does not require acknowledgment.
                                        </small>
                                    </div>
                                </div>
                            )} */}
                        </div>
                    </div>
                </Modal.Body>
            </div>

            <style>{`
                .notification-detail-modal .modal-content {
                    border-radius: 0.7rem;
                    border: none;
                    overflow: hidden;
                }
                .notification-detail-modal .modal-dialog {
                    max-width: 800px;
                }
                .animate-fade-in {
                    animation: fadeIn 0.3s ease-in-out;
                }
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </Modal>
    );
};

export { NotificationDetailModal };